import { useCallback, useState, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import { NoteType } from '../utils/note'
import { useUserState } from '../providers/UserProvider'
import { type User } from '../utils/User'
import { selectedDateToKey } from '../contexts/SelectedDate'
import * as supabase from '../lib/supabase/noteOperations'
import * as cloudkit from '../lib/cloudkit/noteOperations'
import { getUserMetaData, setUserMetaData } from '../lib/supabase/auth'
import { useSubscribeEmailOctopus } from './useSubscribeEmailOctopus'
import { useSaveNote } from './useSaveNote'
import { useCreateNote } from './useCreateNote'
import { fetchUserDefault, setUserDefault } from '../lib/cloudkit/userDefaults'

type NotePath = {
  path: string
  title: string
  parent?: { name: string; id: string }
}

// TODO: fetching all notes is not a very good solution. It's probably easier to check if there are any notes, means fetching just a single note would be enough.
const useStarterNotes = () => {
  const user = useUserState()
  const { subscribeUser } = useSubscribeEmailOctopus()
  const queryClient = useQueryClient()
  const [hasExecuted, setHasExecuted] = useState(false)
  const hasExecutedReference = useRef(false)
  const pendingCreationsReference = useRef<Set<string>>(new Set())

  const createNote = useCreateNote()

  const saveNote = useSaveNote()

  // TODO maybe move this out of this hook? do we need the useCallback?
  const userHasNotes = useCallback(async (currentUser: User) => {
    let hasNotes = false

    if (currentUser?.cloudKitUserId) {
      const hasNotesString = await fetchUserDefault('hasNotes')
      hasNotes = hasNotesString === 'true'
      if (!hasNotes) {
        hasNotes = await cloudkit.hasPrivateNotes()
        await setUserDefault('hasNotes', hasNotes.toString())
      }
    } else if (currentUser?.supabaseUserId) {
      const userMetaData = await getUserMetaData()

      hasNotes = userMetaData?.hasNotes === 'true'
      if (!hasNotes) {
        hasNotes = await supabase.hasPrivateNotes(currentUser.supabaseUserId)
        await setUserMetaData({
          ...userMetaData,
          hasNotes: hasNotes.toString(),
        })
      }
    }

    return hasNotes
  }, [])

  const createDirectories = useCallback(
    (
      directories: { name: string; id: string }[],
      noteType = NoteType.PROJECT_NOTE,
      parentId?: string
    ) => {
      // Filter for unique directories by id
      directories = directories.filter(
        (directory, index, self) =>
          self.findIndex((d) => d.id === directory.id) === index
      )

      const uploadNote = (directory: { name: string; id: string }) => {
        // Check if this directory is already being created
        const creationKey = `dir_${directory.name}_${parentId ?? 'root'}`
        if (pendingCreationsReference.current.has(creationKey)) {
          return // Skip if already pending
        }
        
        // Mark as pending
        pendingCreationsReference.current.add(creationKey)
        
        createNote.mutate({
          recordName: directory.id,
          noteType,
          // eslint-disable-next-line unicorn/no-null -- supabase needs null
          parent: parentId ?? null,
          filename: directory.name,
          isDir: true,
        })
      }

      for (const directory of directories) {
        uploadNote(directory)
      }
    },
    [createNote]
  )

  const createFilesInDirectories = useCallback(
    async (
      textFiles: NotePath[],
      noteType = NoteType.PROJECT_NOTE,
      isCloudKitUser = false
    ) => {
      const uploadNote = async (file: NotePath) => {
        // Check if this file is already being created
        const creationKey = `file_${file.title}_${file.parent?.id ?? 'root'}`
        if (pendingCreationsReference.current.has(creationKey)) {
          return // Skip if already pending
        }
        
        // Mark as pending
        pendingCreationsReference.current.add(creationKey)
        
        const response = await fetch(file.path)
        const data = await response.text()

        // Get the filename from the path
        let filename = file.path.split('/').pop() ?? file.path

        // Remove the extension from the filename as useCreateNote expects filename without extension
        const lastDotIndex = filename.lastIndexOf('.')
        if (lastDotIndex !== -1) {
          filename = filename.slice(0, lastDotIndex)
        }

        // Add parent folder name for CloudKit users with project notes
        if (
          file.parent &&
          noteType === NoteType.PROJECT_NOTE &&
          isCloudKitUser
        ) {
          filename = `${file.parent.name}/${filename}`
        }

        const draft = {
          recordName: uuid(),
          noteType,
          // eslint-disable-next-line unicorn/no-null -- supabase needs null
          parent: file.parent?.id ?? null, // Assign ID of parent in case it's supabase, ignored by CloudKit
          content: data,
          filename,
          title: file.title,
        }

        createNote.mutate(draft)
      }

      for (const file of textFiles) {
        await uploadNote(file)
      }
    },
    [createNote]
  )

  const createDailyNote = useCallback(
    async (
      textFile: NotePath,
      noteType = NoteType.PROJECT_NOTE,
      teamspaceId: string | null,
      isCloudKit: boolean
    ) => {
      const response = await fetch(textFile.path)
      const data = await response.text()

      // In case we are in the process to load the non existing calendar note (it will be loaded blank and might cause a race condition where the blank note is shown first)
      await queryClient.cancelQueries()
      saveNote.mutate(
        {
          noteType,
          parent: teamspaceId,
          content: data,
          filename:
            selectedDateToKey({
              activePeriod: 'day',
              week: 0,
              year: 0,
              date: dayjs(),
            }) + (isCloudKit ? '.txt' : '.md'),
          recordName: uuid(),
          modificationDate: undefined,
          forceCreate: true,
        },
        {
          onSuccess: () => {
            // Now invalidate the query client, so it loads the just saved daily note
            void queryClient.invalidateQueries()
          },
        }
      )
    },
    [queryClient, saveNote]
  )

  // This will always be a supabase call, since it's for Teamspaces
  const createTeamspaceStarterNotes = useCallback(
    async (teamspaceId: string, readyForOpen?: () => void) => {
      if (!teamspaceId) {
        return
      }

      await Promise.all([
        createFilesInDirectories(
          [
            {
              path: '../default-notes/teamspace-start-here.txt',
              title: '👉 Start Here',
              parent: { name: '', id: teamspaceId },
            },
          ],
          NoteType.TEAM_SPACE_NOTE,
          false
        ),
        createDailyNote(
          { path: '../default-notes/teamspace-daily-note.txt', title: '' },
          NoteType.TEAM_SPACE_CALENDAR_NOTE,
          teamspaceId,
          false
        ),
      ])
      readyForOpen?.()

      const textFiles: NotePath[] = [
        {
          path: '../default-notes/teamspace-what-are-projects.txt',
          title: '👨‍💻 What are Projects?',
          parent: { name: '10 - Projects', id: uuid() },
        },
        {
          path: '../default-notes/teamspace-what-are-areas.txt',
          title: '🗺️ What are Areas?',
          parent: { name: '20 - Areas', id: uuid() },
        },
        {
          path: '../default-notes/teamspace-what-are-resources.txt',
          title: '📦 What are Resources?',
          parent: { name: '30 - Resources', id: uuid() },
        },
        {
          path: '../default-notes/teamspace-what-goes-into-archive.txt',
          title: '🗃️ What goes into the Archive?',
          parent: { name: '40 - Archive', id: uuid() },
        },
      ]

      createDirectories(
        textFiles
          .filter(
            (
              file
            ): file is NotePath & { parent: { name: string; id: string } } =>
              file.parent !== undefined && file.parent.id !== teamspaceId
          )
          .map((file) => file.parent),
        NoteType.TEAM_SPACE_NOTE,
        teamspaceId
      )
      await createFilesInDirectories(textFiles, NoteType.TEAM_SPACE_NOTE, false)
    },
    [createDirectories, createFilesInDirectories, createDailyNote]
  )

  // This can be a supabase or cloudkit call
  const createPrivateStarterNotes = useCallback(
    async (started?: () => void, readyForOpen?: () => void) => {
      // Check if already executed using ref (more reliable than state)
      if (!user || hasExecutedReference.current || hasExecuted) {
        return
      }
      
      // Check if user already has notes
      if (await userHasNotes(user)) {
        return
      }
      
      // Mark as executed immediately
      hasExecutedReference.current = true
      setHasExecuted(true)

      started?.()
      const isCloudKit = Boolean(user.cloudKitUserId)

      // If the user wasn't confirmed today, return and don't create starter notes
      if (!isCloudKit && user.supabaseUserId) {
        if (user.confirmed_at) {
          const currentDate = new Date()
          const confirmedDate = new Date(user.confirmed_at)
          if (
            currentDate.getDate() !== confirmedDate.getDate() ||
            currentDate.getMonth() !== confirmedDate.getMonth() ||
            currentDate.getFullYear() !== confirmedDate.getFullYear()
          ) {
            return
          }
        }

        // Subscribe user (only non-Teamspace and only first time)
        void subscribeUser(user.supabaseUserId)
      }

      const resources = { name: '30 - Resources', id: uuid() }
      const textFiles: NotePath[] = [
        {
          path: '../default-notes/what-are-projects.txt',
          title: '👨‍💻 What are Projects?',
          parent: { name: '10 - Projects', id: uuid() },
        },
        {
          path: '../default-notes/what-are-areas.txt',
          title: '🗺️ What are Areas?',
          parent: { name: '20 - Areas', id: uuid() },
        },
        {
          path: '../default-notes/what-are-resources.txt',
          title: '📦 What are Resources?',
          parent: resources,
        },
        {
          path: '../default-notes/how-does-noteplan-work.txt',
          title: '🙋‍♂️ How does NotePlan work?',
          parent: resources,
        },
        {
          path: '../default-notes/what-goes-into-archive.txt',
          title: '🗃️ What goes into the Archive?',
          parent: { name: '40 - Archive', id: uuid() },
        },
      ]

      await createDailyNote(
        { path: '../default-notes/daily-note.txt', title: '' },
        NoteType.CALENDAR_NOTE,
        // eslint-disable-next-line unicorn/no-null -- supabase needs null
        null,
        isCloudKit
      )
      await createFilesInDirectories(
        [
          {
            path: '../default-notes/start-here.txt',
            title: '👉 Start Here',
            parent: undefined,
          },
        ],
        NoteType.PROJECT_NOTE,
        Boolean(user.cloudKitUserId)
      )
      readyForOpen?.()

      createDirectories(
        textFiles
          .filter(
            (
              file
            ): file is NotePath & { parent: { name: string; id: string } } =>
              file.parent !== undefined
          )
          .map((file) => file.parent),
        NoteType.PROJECT_NOTE
      )

      await createFilesInDirectories(
        textFiles,
        NoteType.PROJECT_NOTE,
        isCloudKit
      )

      // Special folders (@Templates, @Archive, @Trash) are now created
      // by SidebarProvider to avoid duplicates
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createDirectories,
      createFilesInDirectories,
      userHasNotes,
      createDailyNote,
      user,
    ]
  )

  return {
    createPrivateStarterNotes,
    createTeamspaceStarterNotes,
    userHasNotes,
  }
}

export default useStarterNotes
